import React, { useState, useEffect } from 'react';
import { artikelPublicService } from '../../services/artikelPublicService';
import {
    FaPlay, FaImage, FaTimes, FaCalendarAlt, FaInfoCircle,
    FaSpinner, FaExclamationTriangle, FaArrowRight, FaUser
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const DokumentasiKegiatan = () => {
    const [dokumentasiKegiatan, setDokumentasiKegiatan] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        loadDokumentasi();
    }, []);

    const loadDokumentasi = async () => {
        setLoading(true);
        setError('');

        const result = await artikelPublicService.getArtikels({
            kategori: 'kegiatan',
            per_page: 6,
        });

        if (result.success) {
            const data = result.data.data || result.data || [];
            setDokumentasiKegiatan(Array.isArray(data) ? data.slice(0, 6) : []);
        } else {
            setError(result.message);
        }
        setLoading(false);
    };

    const getImageUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return path;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    const stripHtml = (html) => {
        if (!html) return '';
        return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    };

    const truncateText = (text, maxLength = 150) => {
        if (!text) return '';
        return text.length > maxLength
            ? text.substring(0, maxLength) + '...'
            : text;
    };

    // ============ LOADING STATE ============
    if (loading) {
        return (
            <section className="py-16 bg-[#F8FAFC]">
                <div className="container mx-auto px-4">
                    <h2 className="section-title">Dokumentasi Kegiatan</h2>
                    <div className="text-center py-12">
                        <FaSpinner className="animate-spin text-4xl text-[#2E7D32] mx-auto mb-4" />
                        <p className="text-gray-500">Memuat dokumentasi...</p>
                    </div>
                </div>
            </section>
        );
    }

    // ============ ERROR STATE ============
    if (error) {
        return (
            <section className="py-16 bg-[#F8FAFC]">
                <div className="container mx-auto px-4">
                    <h2 className="section-title">Dokumentasi Kegiatan</h2>
                    <div className="text-center py-12">
                        <FaExclamationTriangle className="text-5xl text-yellow-500 mx-auto mb-4" />
                        <p className="text-gray-500 mb-4">{error}</p>
                        <button onClick={loadDokumentasi} className="btn-primary">
                            Coba Lagi
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    // ============ EMPTY STATE ============
    if (dokumentasiKegiatan.length === 0) {
        return (
            <section className="py-16 bg-[#F8FAFC]">
                <div className="container mx-auto px-4">
                    <h2 className="section-title">Dokumentasi Kegiatan</h2>
                    <div className="text-center py-12">
                        <FaImage className="text-6xl text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">Belum ada dokumentasi kegiatan</p>
                    </div>
                </div>
            </section>
        );
    }

    // ============ DOKUMENTASI LIST ============
    return (
        <section className="py-16 bg-[#F8FAFC]">
            <div className="container mx-auto px-4">
                <div className="flex items-center justify-between mb-8">
                    <h2 className="section-title !mb-0 !text-left">Dokumentasi Kegiatan</h2>
                    <Link
                        to="/informasi/artikel/kegiatan"
                        className="hidden md:flex items-center gap-2 text-[#2E7D32] hover:text-[#4CAF50] font-semibold transition-colors"
                    >
                        Lihat Semua
                        <FaArrowRight />
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {dokumentasiKegiatan.map((item) => (
                        <div
                            key={item.id}
                            className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer hover:shadow-2xl transition-all duration-300 group"
                            onClick={() => setSelectedItem(item)}
                        >
                            <div className="relative overflow-hidden h-56">
                                {item.gambar || item.thumbnail ? (
                                    <img
                                        src={getImageUrl(item.gambar || item.thumbnail)}
                                        alt={item.judul}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        onError={(e) => {
                                            e.target.src = 'https://placehold.co/400x300/0F4C3A/FFFFFF?text=Kegiatan';
                                        }}
                                    />
                                ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] flex items-center justify-center">
                                        <FaImage className="text-5xl text-white/50" />
                                    </div>
                                )}

                               
                                {/* Overlay saat hover */}
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                    <span className="text-white font-medium bg-white/20 px-4 py-2 rounded-full backdrop-blur-sm">
                                        Lihat Foto
                                    </span>
                                </div>
                            </div>

                            <div className="p-4">
                                <h3 className="font-semibold text-[#0F4C3A] mb-2 line-clamp-2 group-hover:text-[#2E7D32] transition-colors">
                                    {item.judul}
                                </h3>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500 flex items-center gap-1">
                                        <FaCalendarAlt className="text-xs text-[#2E7D32]" />
                                        {formatDate(item.published_at || item.created_at)}
                                    </span>
                                    <span className="text-xs text-gray-400">
                                        🖼️ Foto
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Lihat Semua - Mobile */}
                <div className="text-center mt-8 md:hidden">
                    <Link
                        to="/informasi/artikel/kegiatan"
                        className="inline-flex items-center gap-2 text-[#2E7D32] hover:text-[#4CAF50] font-semibold"
                    >
                        Lihat Semua Dokumentasi
                        <FaArrowRight />
                    </Link>
                </div>
            </div>

            {/* ============ MODAL DETAIL ============ */}
            {selectedItem && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
                    onClick={() => setSelectedItem(null)}
                >
                    <div
                        className="relative max-w-2xl w-full bg-white rounded-xl overflow-hidden max-h-[90vh] flex flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Tombol Close */}
                        <button
                            onClick={() => setSelectedItem(null)}
                            className="absolute top-3 right-3 z-10 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition"
                        >
                            <FaTimes size={20} />
                        </button>

                        {/* Gambar */}
                        <div className="bg-black flex-shrink-0">
                            <img
                                src={getImageUrl(selectedItem.gambar || selectedItem.thumbnail)}
                                alt={selectedItem.judul}
                                className="w-full max-h-[60vh] object-contain mx-auto"
                                onError={(e) => {
                                    e.target.src = 'https://placehold.co/800x600/0F4C3A/FFFFFF?text=Kegiatan';
                                }}
                            />
                        </div>

                        {/* Informasi */}
                        <div className="p-4 md:p-6 bg-white overflow-y-auto">
                            <h3 className="text-xl font-bold text-[#0F4C3A] mb-2">
                                {selectedItem.judul}
                            </h3>

                            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
                                <span className="flex items-center gap-1">
                                    <FaCalendarAlt className="text-[#2E7D32]" />
                                    {formatDate(selectedItem.published_at || selectedItem.created_at)}
                                </span>
                                {selectedItem.author && (
                                    <span className="flex items-center gap-1">
                                        <FaUser className="text-[#2E7D32]" />
                                        {selectedItem.author}
                                    </span>
                                )}
                                <span className="bg-gray-100 px-2 py-0.5 rounded-full capitalize">
                                    {selectedItem.kategori}
                                </span>
                            </div>

                            {selectedItem.ringkasan && (
                                <p className="text-gray-700 border-t border-gray-100 pt-3 mb-3">
                                    <FaInfoCircle className="inline text-[#2E7D32] mr-1" />
                                    {stripHtml(selectedItem.ringkasan)}
                                </p>
                            )}

                            {selectedItem.konten && (
                                <div
                                    className="prose max-w-none text-gray-600 text-sm border-t border-gray-100 pt-3"
                                    dangerouslySetInnerHTML={{
                                        __html: stripHtml(selectedItem.konten).substring(0, 500)
                                    }}
                                />
                            )}

                            {/* Link ke detail lengkap */}
                            <div className="mt-4 pt-4 border-t border-gray-100">
                                <Link
                                    to={`/informasi/artikel/detail/${selectedItem.id}/${selectedItem.slug}`}
                                    className="inline-flex items-center gap-2 text-[#2E7D32] hover:text-[#4CAF50] font-semibold transition-colors"
                                    onClick={() => setSelectedItem(null)}
                                >
                                    Baca Selengkapnya
                                    <FaArrowRight className="text-xs" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default DokumentasiKegiatan;